/**
 * Live changes for the basket (T07-23, design/02 §7): a card in the basket sold / taken elsewhere
 * → `onSold(lineIds)` at once (the pay buttons close within a second — no refetch needed for that),
 * then the basket is refetched from the server; `cart.changed` of this basket (another screen, a
 * hold that ran out) → refetch; a `resync` (events missed), the tab coming back or the network
 * returning → refetch + `GET /pos/carts/{id}/recheck`.
 */
import { useRealtime } from "@tcg/realtime/react";
import { useEffect, useRef } from "react";

import { POS_TOPICS, soldElsewhere, type CartChange, type InventoryChange } from "@/lib/sse";
import type { Cart } from "@/lib/types";

export interface AvailabilityStreamOptions {
  cart: Cart | undefined;
  onSold: (lineIds: string[], change: InventoryChange) => void;
  onCartChanged: (change: CartChange) => void;
  /** Refetch + recheck (resync / visible again / online again). */
  onRecheck: () => void;
  enabled?: boolean;
}

/** The unit lines of `cart` that an inventory change is about. */
export function linesHit(cart: Cart | undefined, change: InventoryChange): string[] {
  if (!cart) {
    return [];
  }
  return cart.lines
    .filter((line) => {
      if (line.kind !== "unit") {
        return false;
      }
      if (change.unitId && line.unitId) {
        return change.unitId === line.unitId;
      }
      return line.variantId === change.variantId;
    })
    .map((line) => line.id);
}

export function useAvailabilityStream(options: AvailabilityStreamOptions) {
  const { cart, onSold, onCartChanged, onRecheck, enabled = true } = options;
  const latest = useRef({ cart, onSold, onCartChanged, onRecheck });
  useEffect(() => {
    latest.current = { cart, onSold, onCartChanged, onRecheck };
  });

  const status = useRealtime(
    [POS_TOPICS.inventory, POS_TOPICS.cart],
    (message) => {
      const { cart: current } = latest.current;
      if (message.topic === POS_TOPICS.inventory) {
        const change = message.payload as InventoryChange;
        const hit = linesHit(current, change);
        if (hit.length === 0) {
          return;
        }
        if (soldElsewhere(change)) {
          latest.current.onSold(hit, change);
        }
        latest.current.onCartChanged({ cartId: current?.id ?? "" });
      } else if (message.topic === POS_TOPICS.cart) {
        const change = message.payload as CartChange;
        if (current && change.cartId === current.id) {
          latest.current.onCartChanged(change);
        }
      }
    },
    { enabled, onResync: () => latest.current.onRecheck() },
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const again = () => {
      if (document.visibilityState === "visible") {
        latest.current.onRecheck();
      }
    };
    document.addEventListener("visibilitychange", again);
    window.addEventListener("online", again);
    return () => {
      document.removeEventListener("visibilitychange", again);
      window.removeEventListener("online", again);
    };
  }, [enabled]);

  return status;
}
